import { createPortal } from 'react-dom';
import DateInput from '@/src/components/date-input';
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Loader2, Plus, Trash2, X, Upload, AlertCircle, ChevronDown, ChevronRight, ArrowRight, Link2, Search, Flag,
  MessageSquare, Calendar, Check, MoreHorizontal, Pencil, Send, Users, List as ListIcon, CalendarDays, CheckSquare,
} from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { zonedLocalToIso, timeAgo } from '@/src/lib/format';
import { useTasks, type TaskPreset, type TaskRow, type TaskList, type TaskMode, type TaskComment } from '@/src/hooks/useTasks';
import { useTeamAccess } from '@/src/hooks/useTeamAccess';
import { useOrgId } from '@/src/hooks/useOrgId';
import type { AppRole } from '@/src/lib/types';
import { useAppColors } from '@/src/lib/theme';
import { linkButtonLabel, listLinkableDocuments, openTaskLink, type TaskLink } from '@/src/lib/taskLinks';

interface TasksPageProps {
  venueId: string;
  userId: string;
  userRole: AppRole;
  /** Open the new-task form already filled in (e.g. "Make a task" on a Team Chats message). */
  preset?: TaskPreset | null;
  onPresetUsed?: () => void;
}

type Member = { id: string; name: string };

interface CSVTask {
  title: string;
  priority: string;
  due_at: string | null;
  assigned_to: string | null;
}

function parseCSV(text: string, members: { id: string; name: string }[], tz = 'Australia/Sydney'): CSVTask[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/['"]/g, ''));
  const titleIdx = headers.findIndex(h => ['title', 'name', 'task', 'summary', 'card name'].includes(h));
  const priorityIdx = headers.findIndex(h => ['priority', 'urgency', 'labels', 'label'].includes(h));
  const dueIdx = headers.findIndex(h => ['due', 'due_date', 'due date', 'deadline', 'duedate'].includes(h));
  const assigneeIdx = headers.findIndex(h => ['assignee', 'assigned_to', 'assigned to', 'owner', 'person', 'members', 'member'].includes(h));
  const listIdx = headers.findIndex(h => ['list', 'list name', 'status', 'column'].includes(h));
  if (titleIdx === -1) return [];
  return lines.slice(1).filter(l => l.trim()).map(line => {
    const cols: string[] = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') { inQuotes = !inQuotes; }
      else if (char === ',' && !inQuotes) { cols.push(current.trim()); current = ''; }
      else { current += char; }
    }
    cols.push(current.trim());
    const title = cols[titleIdx]?.replace(/['"]/g, '') ?? '';
    if (!title) return null;
    const list = listIdx !== -1 ? (cols[listIdx] ?? '').toLowerCase() : '';
    if (list === 'done' || list === 'complete' || list === 'completed') return null;
    const rawPriority = (cols[priorityIdx] ?? '').toLowerCase();
    let priority = 'normal';
    if (['urgent', 'high', 'critical'].some(p => rawPriority.includes(p))) priority = 'urgent';
    else if (['medium', 'normal'].some(p => rawPriority.includes(p))) priority = 'normal';
    else if (['low', 'minor'].some(p => rawPriority.includes(p))) priority = 'low';
    else if (list === 'doing' || list === 'in progress') priority = 'high';
    let due_at: string | null = null;
    if (dueIdx !== -1 && cols[dueIdx]) {
      const raw = cols[dueIdx].trim().replace(/['"]/g, '');
      const pad = (v: string) => v.padStart(2, '0');
      const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/);
      const au = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2}))?/);
      try {
        if (iso) due_at = zonedLocalToIso(`${iso[1]}-${pad(iso[2])}-${pad(iso[3])}${iso[4] ? `T${pad(iso[4])}:${iso[5]}` : ''}`, tz);
        else if (au) due_at = zonedLocalToIso(`${au[3]}-${pad(au[2])}-${pad(au[1])}${au[4] ? `T${pad(au[4])}:${au[5]}` : ''}`, tz);
        else { const d = new Date(raw); if (!Number.isNaN(d.getTime())) due_at = d.toISOString(); }
      } catch { /* leave empty */ }
    }
    let assigned_to: string | null = null;
    if (assigneeIdx !== -1 && cols[assigneeIdx]) {
      const name = cols[assigneeIdx].toLowerCase().replace(/['"]/g, '');
      const match = members.find(m =>
        m.name.toLowerCase().includes(name) || name.includes(m.name.toLowerCase().split(' ')[0])
      );
      if (match) assigned_to = match.id;
    }
    return { title, priority, due_at, assigned_to };
  }).filter(Boolean) as CSVTask[];
}


// ---------- dates (always in the venue's time zone) ----------
const ymdIn = (d: Date, tz: string) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
function dayDiff(iso: string, tz: string): number {
  const a = Date.parse(`${ymdIn(new Date(iso), tz)}T12:00:00Z`);
  const b = Date.parse(`${ymdIn(new Date(), tz)}T12:00:00Z`);
  return Math.round((a - b) / 86_400_000);
}
function timePart(iso: string, tz: string): string {
  const t = new Intl.DateTimeFormat('en-AU', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(iso)).replace(/\s/g, '').toLowerCase();
  return t === '11:59pm' ? '' : t; // date-only tasks show just the date
}
function dueLabel(iso: string, tz: string): { text: string; tone: 'overdue' | 'today' | 'soon' | 'later' } {
  const d = dayDiff(iso, tz);
  const t = timePart(iso, tz);
  const date = new Intl.DateTimeFormat('en-AU', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(iso));
  if (d < 0) return { text: d === -1 ? 'Yesterday' : d > -7 ? `${-d} days ago` : date, tone: 'overdue' };
  if (d === 0) return { text: t ? `Today ${t}` : 'Today', tone: (Date.parse(iso) < Date.now() && t) ? 'overdue' : 'today' };
  if (d === 1) return { text: t ? `Tomorrow ${t}` : 'Due tomorrow', tone: 'soon' };
  return { text: t ? `${date}, ${t}` : date, tone: 'later' };
}
const DUE_BUCKETS = [
  { id: 'overdue', name: 'Overdue' }, { id: 'today', name: 'Today' }, { id: 'tomorrow', name: 'Tomorrow' },
  { id: 'week', name: 'Next 7 days' }, { id: 'later', name: 'Later' }, { id: 'nodate', name: 'No due date' },
];
function bucketOf(t: TaskRow, tz: string): string {
  if (!t.due_at) return 'nodate';
  const d = dayDiff(t.due_at, tz);
  if (d < 0 || (d === 0 && Date.parse(t.due_at) < Date.now() && timePart(t.due_at, tz))) return 'overdue';
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d <= 7) return 'week';
  return 'later';
}
const PRIORITY_RANK: Record<string, number> = { urgent: 0, high: 1, normal: 2, low: 3 };
const sortOpen = (a: TaskRow, b: TaskRow) =>
  (a.due_at ? Date.parse(a.due_at) : Infinity) - (b.due_at ? Date.parse(b.due_at) : Infinity)
  || (PRIORITY_RANK[a.priority ?? 'normal'] ?? 2) - (PRIORITY_RANK[b.priority ?? 'normal'] ?? 2);

type Group = { id: string; name: string; listId: string | null; open: TaskRow[]; done: TaskRow[]; isList: boolean };

// ---------- page ----------
export default function TasksPage({ venueId, userId, userRole, preset, onPresetUsed }: TasksPageProps) {
  const c = useAppColors();
  const isManager = userRole === 'owner' || userRole === 'manager';
  const [tz, setTz] = useState('Australia/Sydney');
  useEffect(() => {
    void supabase.from('venues').select('timezone').eq('id', venueId).maybeSingle().then(({ data }) => { if (data?.timezone) setTz(data.timezone as string); });
  }, [venueId]);
  const [scope, setScope] = useState<'all' | 'mine'>(isManager ? 'all' : 'mine');
  const [view, setView] = useState<'lists' | 'due'>(() => { try { return (localStorage.getItem('opsalo.tasks.view') as 'lists' | 'due') || 'lists'; } catch { return 'lists'; } });
  const [listFilter, setListFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [showDone, setShowDone] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [form, setForm] = useState<{ task?: TaskRow; listId?: string | null; preset?: TaskPreset } | null>(null);
  useEffect(() => { if (preset && isManager) { setForm({ preset }); onPresetUsed?.(); } }, [preset]); // eslint-disable-line react-hooks/exhaustive-deps
  const [importOpen, setImportOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { orgId } = useOrgId(venueId);
  const { members, activeMembers, onOpsAlo } = useTeamAccess(orgId, venueId);
  const t = useTasks(venueId, scope === 'mine' ? { mineOnly: true, userId } : { userId });
  const nameOf = useMemo(() => new Map(members.map((m) => [m.id, m.name])), [members]);
  useEffect(() => { try { localStorage.setItem('opsalo.tasks.view', view); } catch { /* ignore */ } }, [view]);

  const q = search.trim().toLowerCase();
  const visible = t.tasks.filter((x) => !q || x.title.toLowerCase().includes(q) || (x.description ?? '').toLowerCase().includes(q)
    || x.assignees.some((a) => (nameOf.get(a.user_id) ?? '').toLowerCase().includes(q)));
  const openTasks = visible.filter((x) => x.status !== 'done');

  // Groups: by list (lists view) or by when it's due.
  let groups: Group[];
  if (view === 'due') {
    groups = DUE_BUCKETS.map((b) => ({ id: b.id, name: b.name, listId: null, isList: false,
      open: openTasks.filter((x) => bucketOf(x, tz) === b.id).sort(sortOpen), done: [] as TaskRow[] }))
      .filter((g) => g.open.length > 0);
  } else {
    const all: Group[] = [
      ...t.lists.map((l) => ({ id: l.id, name: l.name, listId: l.id as string | null, isList: true, open: [] as TaskRow[], done: [] as TaskRow[] })),
      { id: 'none', name: t.lists.length ? 'No list' : 'All tasks', listId: null, isList: false, open: [], done: [] },
    ];
    for (const x of visible) {
      const g = (x.list_id ? all.find((gg) => gg.isList && gg.listId === x.list_id) : undefined) ?? all[all.length - 1];
      (x.status === 'done' ? g.done : g.open).push(x);
    }
    all.forEach((g) => { g.open.sort(sortOpen); g.done.sort((a, b) => Date.parse(b.completed_at ?? '0') - Date.parse(a.completed_at ?? '0')); });
    const shown = listFilter === 'all' ? all : all.filter((g) => g.id === listFilter);
    groups = shown.filter((g) => g.isList || g.open.length || g.done.length || listFilter === 'none' || t.lists.length === 0);
  }

  const countFor = (id: string) => openTasks.filter((x) => (id === 'all' ? true : id === 'none' ? !x.list_id : x.list_id === id)).length;
  const toggleSet = (set: Set<string>, id: string, fn: (s: Set<string>) => void) => { const n = new Set(set); if (n.has(id)) n.delete(id); else n.add(id); fn(n); };

  const newList = async () => {
    const name = window.prompt('Name the new list (e.g. Opening, Closing, Kitchen)');
    if (!name?.trim()) return;
    const r = await t.createList(name);
    if (r.error) setError(r.error);
  };
  const renameList = async (l: TaskList) => { const name = window.prompt('Rename list', l.name); if (name?.trim() && name.trim() !== l.name) await t.renameList(l.id, name.trim()); };
  const deleteList = async (l: TaskList) => { if (window.confirm(`Delete the list "${l.name}"? Its tasks stay, under "No list".`)) { await t.deleteList(l.id); if (listFilter === l.id) setListFilter('all'); } };

  if (t.loading) return <div className="flex items-center justify-center h-full py-20"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>;

  const nothing = groups.length === 0 || groups.every((g) => !g.open.length && !g.done.length && !g.isList);

  return (
    <div className="flex h-full min-h-0">
      {/* Lists panel (desktop) */}
      {view === 'lists' && (
        <aside className="hidden lg:flex flex-col w-60 shrink-0 py-6 px-3 overflow-y-auto" style={{ borderRight: `1px solid ${c.border}` }}>
          <div className="flex items-center justify-between px-3 mb-2">
            <p className="text-sm font-semibold" style={{ color: c.text }}>Task lists</p>
            {isManager && t.rework && <button onClick={() => void newList()} title="New list" className="p-1 rounded-md" style={{ color: c.textDim }}><Plus size={15} /></button>}
          </div>
          <SideItem label="All lists" count={countFor('all')} active={listFilter === 'all'} onClick={() => setListFilter('all')} />
          {t.lists.map((l) => (
            <SideItem key={l.id} label={l.name} count={countFor(l.id)} active={listFilter === l.id} onClick={() => setListFilter(l.id)}
              menu={isManager ? [{ label: 'Rename', onClick: () => void renameList(l) }, { label: 'Delete list', danger: true, onClick: () => void deleteList(l) }] : undefined} />
          ))}
          {t.lists.length > 0 && countFor('none') > 0 && <SideItem label="No list" count={countFor('none')} active={listFilter === 'none'} onClick={() => setListFilter('none')} />}
          {t.lists.length === 0 && isManager && t.rework && (
            <p className="text-[12px] px-3 mt-3 leading-relaxed" style={{ color: c.textFaint }}>Group tasks into lists like Opening, Closing or Kitchen. Tap + to make one.</p>
          )}
        </aside>
      )}

      <div className="flex-1 min-w-0 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 pb-28">
          {/* Header */}
          <div className="flex flex-col gap-3 mb-5">
            <div className="flex items-end justify-between gap-3">
              <div>
                <h1 className="text-2xl font-semibold" style={{ color: c.text }}>Tasks</h1>
                <p className="text-sm mt-0.5" style={{ color: c.textDim }}>{openTasks.length} open{scope === 'mine' ? ' for you' : ''}</p>
              </div>
              {isManager && (
                <div className="flex gap-2">
                  <button onClick={() => setImportOpen((p) => !p)} title="Import from CSV" aria-label="Import from CSV" className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: c.iconBg, color: c.textDim }}><Upload size={16} /></button>
                  <button onClick={() => setForm({ listId: listFilter !== 'all' && listFilter !== 'none' ? listFilter : null })}
                    className="h-10 px-4 rounded-xl text-sm font-semibold flex items-center gap-2" style={{ background: c.accent, color: 'white' }}>
                    <Plus size={16} /> Add task
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex-1 min-w-[180px] flex items-center gap-2 h-10 px-3 rounded-xl" style={{ background: c.iconBg, border: `1px solid ${c.border}` }}>
                <Search size={15} style={{ color: c.textFaint }} />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks or people"
                  className="flex-1 min-w-0 bg-transparent text-sm outline-none" style={{ color: c.text }} />
                {search && <button onClick={() => setSearch('')} style={{ color: c.textFaint }} aria-label="Clear search"><X size={14} /></button>}
              </div>
              {isManager && (
                <Segmented value={scope} onChange={(v) => setScope(v as 'all' | 'mine')} options={[{ value: 'all', label: 'Everyone', icon: <Users size={14} /> }, { value: 'mine', label: 'Mine' }]} />
              )}
              <Segmented value={view} onChange={(v) => setView(v as 'lists' | 'due')} options={[{ value: 'lists', label: 'Lists', icon: <ListIcon size={14} /> }, { value: 'due', label: 'By due date', icon: <CalendarDays size={14} /> }]} />
            </div>
            {/* Lists as chips on small screens */}
            {view === 'lists' && (t.lists.length > 0 || (isManager && t.rework)) && (
              <div className="flex lg:hidden gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4">
                {[{ id: 'all', name: 'All' }, ...t.lists, ...(countFor('none') ? [{ id: 'none', name: 'No list' }] : [])].map((l) => (
                  <button key={l.id} onClick={() => setListFilter(l.id)} className="shrink-0 h-8 px-3 rounded-full text-[13px] font-medium"
                    style={listFilter === l.id ? { background: c.text, color: c.bg } : { background: c.iconBg, color: c.textDim }}>
                    {l.name} <span className="opacity-60">{countFor(l.id)}</span>
                  </button>
                ))}
                {isManager && t.rework && <button onClick={() => void newList()} className="shrink-0 h-8 px-3 rounded-full text-[13px] font-medium" style={{ background: c.iconBg, color: c.text }}>+ New list</button>}
              </div>
            )}
          </div>

          {error && <p className="mb-3 text-[13px] flex items-center gap-2" style={{ color: c.danger }}><AlertCircle size={14} /> {error} <button onClick={() => setError(null)} className="ml-1" style={{ color: c.textFaint }}><X size={13} /></button></p>}
          {!t.rework && isManager && (
            <p className="mb-4 text-[12px] rounded-xl px-3 py-2" style={{ background: c.iconBg, color: c.textDim }}>Lists, several people per task and comments switch on once the tasks database update has been run.</p>
          )}

          {importOpen && isManager && <CsvImport venueId={venueId} members={members} tz={tz} createTask={t.createTask} onDone={() => setImportOpen(false)} />}

          {nothing ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3" style={{ color: c.textFaint }}>
              <CheckSquare size={34} />
              <p className="text-sm">{q ? 'No tasks match your search.' : scope === 'mine' ? 'Nothing assigned to you right now.' : 'No open tasks.'}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {groups.map((g) => {
                const isCollapsed = collapsed.has(g.id);
                const list = g.isList ? t.lists.find((l) => l.id === g.listId) : undefined;
                return (
                  <section key={g.id}>
                    <div className="flex items-center gap-2 mb-2">
                      <button onClick={() => toggleSet(collapsed, g.id, setCollapsed)} className="flex items-center gap-2 min-w-0">
                        {isCollapsed ? <ChevronRight size={18} style={{ color: c.textDim }} /> : <ChevronDown size={18} style={{ color: c.textDim }} />}
                        <h2 className="text-lg font-semibold truncate" style={{ color: g.id === 'overdue' ? c.danger : c.text }}>{g.name}</h2>
                      </button>
                      <span className="text-[12px] font-medium px-2 py-0.5 rounded-full shrink-0" style={{ background: c.iconBg, color: c.textDim }}>{g.open.length} task{g.open.length === 1 ? '' : 's'}</span>
                      {list && isManager && <Menu items={[{ label: 'Rename list', onClick: () => void renameList(list) }, { label: 'Delete list', danger: true, onClick: () => void deleteList(list) }]} />}
                      {isManager && view === 'lists' && (
                        <button onClick={() => setForm({ listId: g.listId })} title="Add a task to this list" aria-label="Add a task to this list" className="p-1 rounded-md" style={{ color: c.textDim }}><Plus size={16} /></button>
                      )}
                      <div className="flex-1" />
                      {g.done.length > 0 && (
                        <button onClick={() => toggleSet(showDone, g.id, setShowDone)} className="text-[12px] font-medium px-2.5 py-1 rounded-full shrink-0" style={{ background: c.iconBg, color: c.textDim }}>
                          {showDone.has(g.id) ? 'Hide completed' : `${g.done.length} completed`}
                        </button>
                      )}
                    </div>
                    {!isCollapsed && (
                      <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${c.border}`, background: c.bgElevated }}>
                        {[...g.open, ...(showDone.has(g.id) ? g.done : [])].map((x) => (
                          <TaskItem key={x.id} task={x} tz={tz} userId={userId} isManager={isManager} nameOf={nameOf} venueId={venueId}
                            expanded={expandedId === x.id} onToggleExpand={() => setExpandedId(expandedId === x.id ? null : x.id)}
                            onToggleDone={() => void t.toggleDone(x, userId).then((r) => { if (r.error) setError(r.error); })}
                            onEdit={() => setForm({ task: x })} onDelete={() => { if (window.confirm(`Delete "${x.title}"?`)) void t.deleteTask(x.id); }}
                            lists={t.lists} loadComments={t.loadComments} addComment={t.addComment} deleteComment={t.deleteComment} onError={setError} />
                        ))}
                        {g.open.length === 0 && !showDone.has(g.id) && (
                          <p className="px-4 py-3 text-[13px]" style={{ color: c.textFaint }}>{g.done.length ? 'All done here.' : 'No tasks yet.'}</p>
                        )}
                        {isManager && view === 'lists' && <QuickAdd onAdd={(title) => t.createTask({ title, venue_id: venueId, list_id: g.listId }).then((r) => { if (r.error) setError(r.error); })} />}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {form && (
          <TaskForm key={form.task?.id ?? 'new'} venueId={venueId} tz={tz} task={form.task} presetListId={form.listId ?? null} preset={form.preset}
            lists={t.lists} members={onOpsAlo} isManager={isManager} canEach={t.rework}
            onCreateList={async (name) => { const r = await t.createList(name); if (r.error) setError(r.error); return r.id ?? null; }}
            onClose={() => setForm(null)}
            onSave={async (input, ids) => {
              const r = form.task ? await t.updateTask(form.task.id, input, ids) : await t.createTask({ ...input, venue_id: venueId, assigneeIds: ids });
              return r.error;
            }} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- one task row + its details ----------
function TaskItem({ task, tz, userId, isManager, nameOf, venueId, expanded, onToggleExpand, onToggleDone, onEdit, onDelete, lists, loadComments, addComment, deleteComment, onError }: {
  task: TaskRow; tz: string; userId: string; isManager: boolean; nameOf: Map<string, string>; venueId: string; expanded: boolean;
  onToggleExpand: () => void; onToggleDone: () => void; onEdit: () => void; onDelete: () => void; lists: TaskList[];
  loadComments: (id: string) => Promise<TaskComment[]>; addComment: (id: string, uid: string, body: string) => Promise<{ error: string | null }>;
  deleteComment: (id: string, taskId: string) => Promise<void>; onError: (e: string | null) => void;
}) {
  const c = useAppColors();
  const done = task.status === 'done';
  const each = task.mode === 'individual' && task.assignees.length > 1;
  const mine = task.assignees.find((a) => a.user_id === userId);
  const myPartDone = each && !!mine?.completed_at;
  const doneCount = task.assignees.filter((a) => a.completed_at).length;
  const due = task.due_at ? dueLabel(task.due_at, tz) : null;
  const dueColor = due?.tone === 'overdue' ? c.danger : due?.tone === 'today' ? '#3B82F6' : due?.tone === 'soon' ? '#D97706' : c.textDim;
  const flag = task.priority === 'urgent' ? c.danger : task.priority === 'high' ? '#F59E0B' : null;
  const ticked = done || myPartDone;
  const canTick = isManager || !!mine;

  return (
    <div style={{ borderBottom: `1px solid ${c.border}` }}>
      <div className="group flex items-start sm:items-center gap-3 px-3 sm:px-4 py-3 cursor-pointer" onClick={onToggleExpand}
        style={{ background: expanded ? c.iconBg : 'transparent' }}>
        <button onClick={(e) => { e.stopPropagation(); if (canTick) onToggleDone(); }} disabled={!canTick}
          title={done ? 'Mark not done' : each && mine ? (myPartDone ? 'Untick your part' : 'Tick your part') : 'Mark done'}
          aria-label={done ? 'Mark not done' : 'Mark done'}
          className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors mt-0.5 sm:mt-0"
          style={ticked ? { background: done ? c.accent : c.accentBg, color: done ? 'white' : c.accentSoft, border: `1.5px solid ${c.accent}` } : { border: `1.5px solid ${c.borderStrong}`, color: c.textFaint }}>
          {ticked ? <Check size={13} strokeWidth={3} /> : <Check size={12} className="opacity-0 group-hover:opacity-50" />}
        </button>
        <Avatars ids={task.assignees.map((a) => a.user_id)} nameOf={nameOf} done={new Set(task.assignees.filter((a) => a.completed_at).map((a) => a.user_id))} showDone={each} />
        <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
          <span className="text-[15px] truncate" style={{ color: done ? c.textFaint : c.text, textDecoration: done ? 'line-through' : 'none' }}>{task.title}</span>
          <div className="flex items-center gap-3 flex-wrap text-[13px]">
            {task.link && !done && (
              <button onClick={(e) => { e.stopPropagation(); void openTaskLink(task.link!, venueId).then((er) => { if (er) onError(er); }); }}
                className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[12px] font-semibold whitespace-nowrap"
                style={{ background: c.accentBg, color: c.accentSoft, border: `1px solid ${c.accentBorder}` }}>
                {linkButtonLabel(task.link)} <ArrowRight size={11} />
              </button>
            )}
            {due && <span className="flex items-center gap-1 whitespace-nowrap" style={{ color: done ? c.textFaint : dueColor }}><Calendar size={13} /> {due.text}</span>}
            {each && <span className="whitespace-nowrap" style={{ color: c.textDim }}>{doneCount}/{task.assignees.length} done</span>}
            {task.comment_count > 0 && <span className="flex items-center gap-1" style={{ color: c.textDim }}><MessageSquare size={13} /> {task.comment_count}</span>}
            {flag && !done && <Flag size={14} fill={flag} style={{ color: flag }} />}
          </div>
        </div>
        {isManager && (
          <div className="hidden sm:flex opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
            <button onClick={onEdit} title="Edit" aria-label="Edit" className="p-1.5 rounded-lg" style={{ color: c.textFaint }}><Pencil size={14} /></button>
            <button onClick={onDelete} title="Delete" aria-label="Delete" className="p-1.5 rounded-lg" style={{ color: c.textFaint }}><Trash2 size={14} /></button>
          </div>
        )}
      </div>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <TaskDetail task={task} tz={tz} userId={userId} isManager={isManager} nameOf={nameOf} lists={lists}
              onEdit={onEdit} onDelete={onDelete} loadComments={loadComments} addComment={addComment} deleteComment={deleteComment} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TaskDetail({ task, tz, userId, isManager, nameOf, lists, onEdit, onDelete, loadComments, addComment, deleteComment }: {
  task: TaskRow; tz: string; userId: string; isManager: boolean; nameOf: Map<string, string>; lists: TaskList[];
  onEdit: () => void; onDelete: () => void;
  loadComments: (id: string) => Promise<TaskComment[]>; addComment: (id: string, uid: string, body: string) => Promise<{ error: string | null }>;
  deleteComment: (id: string, taskId: string) => Promise<void>;
}) {
  const c = useAppColors();
  const [comments, setComments] = useState<TaskComment[] | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const each = task.mode === 'individual' && task.assignees.length > 1;
  const listName = lists.find((l) => l.id === task.list_id)?.name;
  useEffect(() => { void loadComments(task.id).then(setComments); }, [task.id, task.comment_count, loadComments]);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true); setErr(null);
    const r = await addComment(task.id, userId, text);
    setBusy(false);
    if (r.error) { setErr(r.error); return; }
    setText('');
    setComments(await loadComments(task.id));
  };
  const head = (s: string) => <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: c.textFaint }}>{s}</p>;
  return (
    <div className="px-4 sm:pl-[4.75rem] pr-4 pb-4 pt-1 space-y-4" style={{ background: c.iconBg }}>
      <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: task.description ? c.text : c.textFaint }}>{task.description || 'No details added.'}</p>

      <div className="flex flex-wrap gap-x-6 gap-y-3 text-[13px]" style={{ color: c.textDim }}>
        {task.assignees.length > 0 && (
          <div>
            {head(each ? 'Each person does their own' : task.assignees.length > 1 ? 'Shared: anyone can tick it' : 'For')}
            <div className="flex flex-wrap gap-1.5">
              {task.assignees.map((a) => (
                <span key={a.user_id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px]" style={{ background: c.bgElevated, color: c.text }}>
                  {each && (a.completed_at ? <Check size={11} style={{ color: c.accentSoft }} /> : <span className="w-2 h-2 rounded-full" style={{ background: c.borderStrong }} />)}
                  {nameOf.get(a.user_id) ?? 'Someone'}
                </span>
              ))}
            </div>
          </div>
        )}
        {listName && <div>{head('List')}{listName}</div>}
        {task.due_at && <div>{head('Due')}{new Intl.DateTimeFormat('en-AU', { timeZone: tz, weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).format(new Date(task.due_at))}</div>}
        {task.link && (task.link.type === 'quiz' || task.link.type === 'document') && <div>{head('Opens')}{task.link.label}</div>}
      </div>

      {isManager && (
        <div className="flex gap-2 sm:hidden">
          <button onClick={onEdit} className="h-9 px-3 rounded-lg text-[13px] font-medium flex items-center gap-1.5" style={{ background: c.bgElevated, color: c.text }}><Pencil size={13} /> Edit</button>
          <button onClick={onDelete} className="h-9 px-3 rounded-lg text-[13px] font-medium flex items-center gap-1.5" style={{ background: c.bgElevated, color: c.danger }}><Trash2 size={13} /> Delete</button>
        </div>
      )}

      {/* Comments */}
      <div>
        {head('Comments')}
        {comments === null ? <Loader2 size={14} className="animate-spin" style={{ color: c.textFaint }} /> : (
          <div className="space-y-2.5 mb-3 mt-2">
            {comments.length === 0 && <p className="text-[13px]" style={{ color: c.textFaint }}>No comments yet.</p>}
            {comments.map((cm) => (
              <div key={cm.id} className="group/cm flex gap-2.5">
                <Initial name={nameOf.get(cm.user_id ?? '') ?? '?'} />
                <div className="min-w-0 flex-1">
                  <p className="text-[12px]" style={{ color: c.textDim }}><b style={{ color: c.text }}>{nameOf.get(cm.user_id ?? '') ?? 'Someone'}</b> · {timeAgo(cm.created_at)}</p>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: c.text }}>{cm.body}</p>
                </div>
                {(cm.user_id === userId || isManager) && (
                  <button onClick={() => void deleteComment(cm.id, task.id).then(() => setComments((p) => (p ?? []).filter((x) => x.id !== cm.id)))}
                    className="opacity-0 group-hover/cm:opacity-100 p-1 self-start" style={{ color: c.textFaint }} aria-label="Delete comment"><X size={13} /></button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
            placeholder="Write a comment" maxLength={2000}
            className="flex-1 min-w-0 h-10 px-3 rounded-xl text-sm outline-none" style={{ background: c.bgElevated, color: c.text, border: `1px solid ${c.border}` }} />
          <button onClick={() => void send()} disabled={busy || !text.trim()} className="h-10 w-10 rounded-xl flex items-center justify-center disabled:opacity-40" style={{ background: c.accent, color: 'white' }} aria-label="Send comment">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
          </button>
        </div>
        {err && <p className="text-[12px] mt-1.5" style={{ color: c.danger }}>{err}</p>}
      </div>
    </div>
  );
}

// ---------- create / edit ----------
type FormInput = { title: string; description: string | null; priority: string; due_at: string | null; list_id: string | null; mode: TaskMode; link: TaskLink | null };

function TaskForm({ venueId, tz, task, presetListId, preset, lists, members, isManager, canEach, onCreateList, onClose, onSave }: {
  venueId: string; tz: string; task?: TaskRow; presetListId: string | null; preset?: TaskPreset; lists: TaskList[]; members: Member[]; isManager: boolean; canEach: boolean;
  onCreateList: (name: string) => Promise<string | null>; onClose: () => void;
  onSave: (input: FormInput, assigneeIds: string[]) => Promise<string | null>;
}) {
  const c = useAppColors();
  const localDue = task?.due_at ? new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(task.due_at)).replace(', ', 'T') : '';
  const [title, setTitle] = useState(task?.title ?? preset?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? preset?.description ?? '');
  const [listId, setListId] = useState<string>(task ? task.list_id ?? '' : presetListId ?? '');
  const [people, setPeople] = useState<string[]>(task ? task.assignees.map((a) => a.user_id) : preset?.assigneeIds ?? []);
  const [mode, setMode] = useState<TaskMode>(task?.mode ?? 'shared');
  const [dueDate, setDueDate] = useState(localDue ? localDue.slice(0, 10) : preset?.dueDate ?? '');
  const [dueTime, setDueTime] = useState(localDue && !localDue.endsWith('23:59') ? localDue.slice(11, 16) : preset?.dueTime ?? '17:00');
  const [priority, setPriority] = useState(task?.priority ?? preset?.priority ?? 'normal');
  const [linkType, setLinkType] = useState<'' | TaskLink['type']>(task?.link?.type ?? '');
  const [linkTarget, setLinkTarget] = useState(task?.link?.type === 'quiz' ? task.link.course_id : task?.link?.type === 'document' ? `${task.link.bucket}/${task.link.name}` : '');
  const [quizzes, setQuizzes] = useState<Array<{ id: string; title: string }>>([]);
  const [docs, setDocs] = useState<Array<{ bucket: string; name: string; label: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (linkType === 'quiz' && !quizzes.length) void supabase.from('training_courses').select('id, title').eq('venue_id', venueId).eq('active', true).order('created_at', { ascending: false }).then(({ data }) => setQuizzes((data ?? []) as Array<{ id: string; title: string }>));
    if (linkType === 'document' && !docs.length) void listLinkableDocuments(venueId).then(setDocs);
    // Quizzes and reports are something each person does themselves.
    if (!task && (linkType === 'quiz' || linkType === 'eod_report')) setMode('individual');
  }, [linkType, venueId, quizzes.length, docs.length, task]);

  const buildLink = (): TaskLink | null => {
    if (linkType === 'quiz') {
      const q = quizzes.find((x) => x.id === linkTarget);
      if (q) return { type: 'quiz', course_id: q.id, label: q.title };
      return task?.link?.type === 'quiz' && task.link.course_id === linkTarget ? task.link : null;
    }
    if (linkType === 'document') {
      const d = docs.find((x) => `${x.bucket}/${x.name}` === linkTarget);
      if (d) return { type: 'document', bucket: d.bucket, name: d.name, label: d.label };
      return task?.link?.type === 'document' && `${task.link.bucket}/${task.link.name}` === linkTarget ? task.link : null;
    }
    if (linkType === 'eod_report') return { type: 'eod_report', label: 'EOD report' };
    if (linkType === 'nightly_numbers') return { type: 'nightly_numbers', label: 'Nightly numbers' };
    return null;
  };

  const save = async () => {
    if (!title.trim()) return;
    const link = buildLink();
    if ((linkType === 'quiz' || linkType === 'document') && !link) { setErr(`Pick the ${linkType === 'quiz' ? 'quiz' : 'document'}, or set "Opens" to Nothing.`); return; }
    let lid = listId;
    if (lid === '__new') {
      const name = window.prompt('Name the new list');
      lid = name?.trim() ? (await onCreateList(name.trim())) ?? '' : '';
    }
    setSaving(true); setErr(null);
    const due = dueDate ? zonedLocalToIso(`${dueDate}T${dueTime || '23:59'}`, tz) : null;
    const e = await onSave({ title: title.trim(), description: description.trim() || null, priority, due_at: due, list_id: lid || null, mode: people.length > 1 ? mode : 'shared', link }, people);
    setSaving(false);
    if (e) { setErr(e); return; }
    onClose();
  };

  const field = { background: c.iconBg, color: c.text, border: `1px solid ${c.border}` };
  const label = (s: string) => <span className="block text-[12px] font-medium mb-1.5" style={{ color: c.textDim }}>{s}</span>;
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-[60]" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }} transition={{ type: 'spring', stiffness: 400, damping: 32 }}
        className="modal-panel fixed inset-x-3 top-6 bottom-6 sm:inset-x-auto sm:left-1/2 sm:-translate-x-1/2 sm:w-[600px] rounded-2xl z-[60] flex flex-col overflow-hidden"
        style={{ background: c.bg, border: `1px solid ${c.border}` }}>
        <header className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${c.border}` }}>
          <h2 className="text-base font-semibold" style={{ color: c.text }}>{task ? 'Edit task' : 'New task'}</h2>
          <button onClick={onClose} className="p-2 -m-1" style={{ color: c.textDim }} aria-label="Close"><X size={18} /></button>
        </header>
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 py-4 space-y-4">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs doing?" maxLength={200}
            className="w-full h-11 px-3 rounded-xl text-[15px] outline-none" style={field} />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Details or instructions (optional)"
            className="w-full px-3 py-2 rounded-xl text-sm outline-none resize-y" style={field} />

          <div>
            {label('People')}
            <div className="flex flex-wrap gap-1.5">
              {members.map((m) => {
                const on = people.includes(m.id);
                return (
                  <button key={m.id} type="button" onClick={() => setPeople((p) => (on ? p.filter((x) => x !== m.id) : [...p, m.id]))}
                    className="px-2.5 py-1 rounded-full text-[13px]" style={on ? { background: c.accentBg, color: c.accentSoft, border: `1px solid ${c.accentBorder}` } : { background: c.iconBg, color: c.textDim, border: `1px solid ${c.border}` }}>
                    {m.name}
                  </button>
                );
              })}
            </div>
            {people.length === 0 && <p className="text-[12px] mt-1.5" style={{ color: c.textFaint }}>No one yet. Anyone can pick it up.</p>}
          </div>

          {people.length > 1 && canEach && (
            <div>
              {label(`${people.length} people: how is it done?`)}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {([['shared', 'Shared', 'One job. Done when anyone ticks it.'], ['individual', 'Each person', 'Everyone ticks their own. Shows 2/5 done.']] as const).map(([v, t1, t2]) => (
                  <button key={v} type="button" onClick={() => setMode(v)} className="text-left rounded-xl px-3 py-2.5"
                    style={mode === v ? { background: c.accentBg, border: `1.5px solid ${c.accent}` } : { background: c.iconBg, border: `1px solid ${c.border}` }}>
                    <p className="text-sm font-semibold" style={{ color: c.text }}>{t1}</p>
                    <p className="text-[12px]" style={{ color: c.textDim }}>{t2}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">{label('List')}
              <select value={listId} onChange={(e) => setListId(e.target.value)} disabled={!canEach} className="w-full h-10 px-3 rounded-xl text-sm outline-none disabled:opacity-40" style={field}>
                <option value="">No list</option>
                {lists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                {isManager && canEach && <option value="__new">+ New list…</option>}
              </select>
            </label>
            <div>{label('Priority')}
              <div className="flex rounded-xl p-1 gap-1" style={{ background: c.iconBg }}>
                {['low', 'normal', 'high', 'urgent'].map((p) => (
                  <button key={p} type="button" onClick={() => setPriority(p)} className="flex-1 h-8 rounded-lg text-[12px] font-medium capitalize"
                    style={priority === p ? { background: c.bgElevated, color: p === 'urgent' ? c.danger : p === 'high' ? '#D97706' : c.text, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' } : { color: c.textDim }}>{p}</button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-3">
            <label className="block">{label('Due date')}
              <DateInput value={dueDate} onChange={setDueDate} clearable ariaLabel="Due date" className="flex w-full h-10 px-3 rounded-xl text-sm" style={{ ...field, colorScheme: c.isDark ? 'dark' : 'light' }} />
            </label>
            <label className="block">{label('Time')}
              <input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} disabled={!dueDate} className="w-full h-10 px-3 rounded-xl text-sm outline-none disabled:opacity-40" style={{ ...field, colorScheme: c.isDark ? 'dark' : 'light' }} />
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block"><span className="flex items-center gap-1.5 text-[12px] font-medium mb-1.5" style={{ color: c.textDim }}><Link2 size={12} /> Opens (optional)</span>
              <select value={linkType} onChange={(e) => { setLinkType(e.target.value as '' | TaskLink['type']); setLinkTarget(''); }} className="w-full h-10 px-3 rounded-xl text-sm outline-none" style={field}>
                <option value="">Nothing</option>
                <option value="quiz">A quiz</option>
                <option value="document">A document</option>
                <option value="eod_report">The EOD report</option>
                <option value="nightly_numbers">Nightly numbers (managers)</option>
              </select>
            </label>
            {linkType === 'quiz' && (
              <label className="block">{label('Which quiz')}
                <select value={linkTarget} onChange={(e) => setLinkTarget(e.target.value)} className="w-full h-10 px-3 rounded-xl text-sm outline-none" style={field}>
                  <option value="">{quizzes.length ? 'Choose a quiz…' : 'No quizzes yet'}</option>
                  {quizzes.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
                </select>
              </label>
            )}
            {linkType === 'document' && (
              <label className="block">{label('Which document')}
                <select value={linkTarget} onChange={(e) => setLinkTarget(e.target.value)} className="w-full h-10 px-3 rounded-xl text-sm outline-none" style={field}>
                  <option value="">{docs.length ? 'Choose a document…' : 'Loading documents…'}</option>
                  {docs.map((d) => <option key={`${d.bucket}/${d.name}`} value={`${d.bucket}/${d.name}`}>{d.label}</option>)}
                </select>
              </label>
            )}
          </div>
          {(linkType === 'quiz' || linkType === 'eod_report' || linkType === 'nightly_numbers') && (
            <p className="text-[12px] -mt-2" style={{ color: c.textFaint }}>It ticks itself off when {linkType === 'quiz' ? 'they pass the quiz' : 'they submit it'}.</p>
          )}
          {err && <p className="text-[13px]" style={{ color: c.danger }}>{err}</p>}
        </div>
        <div className="px-5 py-4" style={{ borderTop: `1px solid ${c.border}` }}>
          <button onClick={() => void save()} disabled={!title.trim() || saving} className="w-full h-11 rounded-xl text-sm font-semibold disabled:opacity-40 flex items-center justify-center gap-2" style={{ background: c.accent, color: 'white' }}>
            {saving && <Loader2 size={15} className="animate-spin" />}{task ? 'Save changes' : people.length > 1 ? `Create 1 task for ${people.length} people` : 'Create task'}
          </button>
        </div>
      </motion.div>
    </>
  );
}

// ---------- small pieces ----------
function QuickAdd({ onAdd }: { onAdd: (title: string) => Promise<void> }) {
  const c = useAppColors();
  const [on, setOn] = useState(false);
  const [text, setText] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (on) ref.current?.focus(); }, [on]);
  const go = async () => { const v = text.trim(); if (!v) { setOn(false); return; } setText(''); await onAdd(v); ref.current?.focus(); };
  return on ? (
    <div className="flex items-center gap-3 px-4 py-2.5" style={{ borderTop: `1px solid ${c.border}` }}>
      <Plus size={16} style={{ color: c.textFaint }} />
      <input ref={ref} value={text} onChange={(e) => setText(e.target.value)} onBlur={() => { if (!text.trim()) setOn(false); }}
        onKeyDown={(e) => { if (e.key === 'Enter') void go(); if (e.key === 'Escape') { setText(''); setOn(false); } }}
        placeholder="Task name, then Enter" className="flex-1 bg-transparent text-sm outline-none" style={{ color: c.text }} />
    </div>
  ) : (
    <button onClick={() => setOn(true)} className="w-full flex items-center gap-3 px-4 py-3 text-sm" style={{ color: c.textDim, borderTop: `1px solid ${c.border}` }}>
      <Plus size={16} /> Add a task
    </button>
  );
}

function Initial({ name, size = 26, ring }: { name: string; size?: number; ring?: string }) {
  const c = useAppColors();
  const initials = name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase() || '?';
  return (
    <span className="rounded-full flex items-center justify-center font-semibold shrink-0" title={name}
      style={{ width: size, height: size, fontSize: size * 0.38, background: c.accentBg, color: c.accentSoft, border: `2px solid ${ring ?? c.bgElevated}` }}>
      {initials}
    </span>
  );
}

function Avatars({ ids, nameOf, done, showDone }: { ids: string[]; nameOf: Map<string, string>; done: Set<string>; showDone: boolean }) {
  const c = useAppColors();
  if (!ids.length) return <span className="w-[26px] h-[26px] rounded-full shrink-0" style={{ border: `1.5px dashed ${c.borderStrong}` }} title="No one assigned" />;
  const shown = ids.slice(0, 3);
  return (
    <div className="flex -space-x-2 shrink-0" title={ids.map((i) => nameOf.get(i) ?? 'Someone').join(', ')}>
      {shown.map((id) => <Initial key={id} name={nameOf.get(id) ?? '?'} ring={showDone && done.has(id) ? c.accent : undefined} />)}
      {ids.length > 3 && <span className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-[10px] font-semibold" style={{ background: c.iconBg, color: c.textDim, border: `2px solid ${c.bgElevated}` }}>+{ids.length - 3}</span>}
    </div>
  );
}

function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string; icon?: React.ReactNode }> }) {
  const c = useAppColors();
  return (
    <div className="flex rounded-xl p-1 gap-1 shrink-0" style={{ background: c.iconBg }}>
      {options.map((o) => (
        <button key={o.value} onClick={() => onChange(o.value)} className="h-8 px-3 rounded-lg text-[13px] font-medium flex items-center gap-1.5"
          style={value === o.value ? { background: c.bgElevated, color: c.text, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' } : { color: c.textDim }}>
          {o.icon}{o.label}
        </button>
      ))}
    </div>
  );
}

function Menu({ items }: { items: Array<{ label: string; onClick: () => void; danger?: boolean }> }) {
  const c = useAppColors();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const W = 176;
  const toggle = () => {
    if (pos) { setPos(null); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    // Open below the dots, kept inside the window (no longer cut off by the sidebar).
    const left = Math.max(8, Math.min(r.left, window.innerWidth - W - 8));
    const height = items.length * 40 + 8;
    const top = r.bottom + 6 + height > window.innerHeight ? Math.max(8, r.top - height - 6) : r.bottom + 6;
    setPos({ top, left });
  };
  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => { window.removeEventListener('resize', close); window.removeEventListener('scroll', close, true); };
  }, [pos]);
  return (
    <>
      <button ref={btnRef} onClick={toggle} className="p-1 rounded-md" style={{ color: c.textDim }} aria-label="More options" aria-expanded={!!pos}><MoreHorizontal size={16} /></button>
      {pos && createPortal(
        <>
          <div className="fixed inset-0 z-[70]" onClick={() => setPos(null)} />
          <div role="menu" className="fixed z-[71] p-1 rounded-xl shadow-xl" style={{ top: pos.top, left: pos.left, width: W, background: c.bgElevated, border: `1px solid ${c.borderStrong}` }}>
            {items.map((it) => (
              <button key={it.label} role="menuitem" onClick={() => { setPos(null); it.onClick(); }} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:opacity-80"
                style={{ color: it.danger ? c.danger : c.text }}>{it.label}</button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </>
  );
}

function SideItem({ label, count, active, onClick, menu }: { label: string; count: number; active: boolean; onClick: () => void; menu?: Array<{ label: string; onClick: () => void; danger?: boolean }> }) {
  const c = useAppColors();
  return (
    <div className="group flex items-center rounded-lg" style={{ background: active ? c.iconBg : 'transparent' }}>
      <button onClick={onClick} className="flex-1 min-w-0 flex items-center justify-between gap-2 px-3 py-2.5 text-[14px]" style={{ color: active ? c.text : c.textDim, fontWeight: active ? 600 : 400 }}>
        <span className="truncate">{label}</span>
        <span className="text-[12px] px-1.5 rounded-md" style={{ background: c.iconBg, color: c.textDim }}>{count}</span>
      </button>
      {menu && <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100"><Menu items={menu} /></div>}
    </div>
  );
}

function CsvImport({ venueId, members, tz, createTask, onDone }: {
  venueId: string; members: Member[]; tz: string;
  createTask: (i: { title: string; priority?: string; due_at?: string | null; assigneeIds?: string[]; venue_id: string }) => Promise<{ error: string | null }>;
  onDone: () => void;
}) {
  const c = useAppColors();
  const fileRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<CSVTask[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const onFile = (f: File | undefined) => {
    if (!f) return;
    setErr(null);
    const r = new FileReader();
    r.onload = (e) => { const parsed = parseCSV(String(e.target?.result ?? ''), members, tz); if (!parsed.length) setErr('No tasks found. Make sure your CSV has a "title" column.'); setRows(parsed); };
    r.readAsText(f);
  };
  const run = async () => {
    setBusy(true);
    for (const x of rows) {
      const res = await createTask({ title: x.title, priority: x.priority, due_at: x.due_at, assigneeIds: x.assigned_to ? [x.assigned_to] : [], venue_id: venueId });
      if (res.error) { setErr(res.error); setBusy(false); return; }
    }
    setBusy(false); onDone();
  };
  return (
    <div className="rounded-xl p-4 mb-5 space-y-3" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold" style={{ color: c.text }}>Import tasks from a CSV</p>
        <button onClick={onDone} style={{ color: c.textFaint }} aria-label="Close"><X size={16} /></button>
      </div>
      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
      {rows.length === 0 ? (
        <button onClick={() => fileRef.current?.click()} className="w-full h-16 rounded-xl text-sm" style={{ background: c.iconBg, color: c.textDim, border: `1px dashed ${c.borderStrong}` }}>
          Choose a CSV (columns like title, priority, due, assignee)
        </button>
      ) : (
        <>
          <p className="text-[13px]" style={{ color: c.textDim }}>{rows.length} tasks found.</p>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {rows.map((x, i) => <p key={i} className="text-[13px] truncate" style={{ color: c.text }}>• {x.title}</p>)}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setRows([])} className="flex-1 h-10 rounded-xl text-sm" style={{ background: c.iconBg, color: c.textDim }}>Cancel</button>
            <button onClick={() => void run()} disabled={busy} className="flex-1 h-10 rounded-xl text-sm font-semibold disabled:opacity-40" style={{ background: c.accent, color: 'white' }}>{busy ? 'Importing…' : `Import ${rows.length}`}</button>
          </div>
        </>
      )}
      {err && <p className="text-[13px]" style={{ color: c.danger }}>{err}</p>}
    </div>
  );
}
